import {detectVisitorCountry} from './tb-visitor-location.js';
import {convertText, currencyForCountry, formatAmount} from './tb-currency-core.js';

const bar = document.querySelector('tb-display-currency');
if (bar && !window.TBCurrency) {
  const config = JSON.parse(bar.querySelector('[data-tb-currency-config]').textContent);
  const select = bar.querySelector('select');
  const status = bar.querySelector('[role="status"]');
  const day = 86400000;
  const supported = ['USD', 'AED', 'EUR', 'GBP', 'INR'];
  const storage = {
    get(key) { try { return JSON.parse(localStorage.getItem(key)); } catch { return null; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} }
  };
  let rates = {USD:1};
  let currency = 'USD';
  let unavailable = false;
  let manual = false;
  const saved = storage.get('tb-display-currency-v1');
  if (saved?.expires > Date.now() && supported.includes(saved.currency)) manual = true;
  const tracked = new WeakMap();
  const roots = 'product-price,[ref="priceContainer"],.tbp-unit-price,sticky-add-to-cart,[aria-label="Quick add to cart bar"],.tbb-product-card,.tbx-card,.tbb-finder__results,.cart-items__table,.cart-totals,tb-cart-shipping,tb-bulk-economics,.price,.compare-at-price,.unit-price,.price-per-item';
  const excluded = 'script,style,textarea,input,select,option,iframe,[contenteditable="true"],.tbp-economics__inputs,.tbb-finder__filters,[data-tb-currency-native]';
  const fill = (template, code) => template.replaceAll('[currency]', code);
  const writeText = (element, text) => { if (element.textContent !== text) element.textContent = text; };

  function convertNode(node) {
    const parent = node.parentElement;
    if (!parent || parent.closest(excluded)) return;
    const current = node.nodeValue;
    let record = tracked.get(node);
    if (!record || current !== record.rendered) record = {source:current, rendered:current};
    const text = convertText(record.source, currency, rates);
    record.rendered = text;
    tracked.set(node, record);
    if (current !== text) node.nodeValue = text;
  }

  function walk(root) {
    if (root.nodeType === Node.TEXT_NODE) {
      if (root.parentElement?.closest(roots)) convertNode(root);
      return;
    }
    if (!(root instanceof Element) || root.closest(excluded)) return;
    const sections = root.matches(roots) || root.closest(roots) ? [root] : Array.from(root.querySelectorAll(roots));
    const visited = new Set();
    for (const section of sections) {
      const walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (visited.has(node)) continue;
        visited.add(node);
        convertNode(node);
      }
    }
  }

  function updateDisclosure() {
    const text = unavailable ? config.strings.unavailable : currency === 'USD' ? config.strings.base : fill(config.strings.local, currency);
    writeText(status, text);
    select.value = currency;
    for (const label of document.querySelectorAll('[data-testid="localization-currency-code"]')) writeText(label, config.country);
    select.querySelectorAll('option').forEach(option => { option.disabled = option.value !== 'USD' && !(rates[option.value] > 0); });
    for (const actions of document.querySelectorAll('.cart__ctas')) {
      let note = actions.querySelector('[data-tb-currency-cart-note]');
      if (!note) {
        note = document.createElement('p');
        note.dataset.tbCurrencyCartNote = '';
        note.className = 'tb-currency-cart-note';
        actions.prepend(note);
      }
      writeText(note, currency === 'USD' ? config.strings.base : fill(config.strings.cart, currency));
    }
  }

  function setCurrency(next, remember = false) {
    currency = supported.includes(next) && rates[next] > 0 ? next : 'USD';
    if (remember) {
      manual = true;
      storage.set('tb-display-currency-v1', {currency, expires:Date.now() + 180 * day});
    }
    updateDisclosure();
    walk(document.body);
    document.dispatchEvent(new CustomEvent('tb:currency-change', {detail:{currency}}));
  }

  window.TBCurrency = {
    convert(amount, source = 'USD', target = currency) {
      const sourceRate = source === 'USD' ? 1 : rates[source];
      const targetRate = target === 'USD' ? 1 : rates[target];
      if (!Number.isFinite(amount) || !(sourceRate > 0) || !(targetRate > 0)) return null;
      return amount / sourceRate * targetRate;
    },
    format(amount, source = 'USD', precision = 2) {
      const divisor = source === 'USD' ? 1 : rates[source];
      if (divisor > 0) return formatAmount(amount / divisor, currency, rates, precision);
      return new Intl.NumberFormat('en-US', {style:'currency', currency:source, currencyDisplay:'code', minimumFractionDigits:precision, maximumFractionDigits:precision}).format(amount);
    }
  };

  async function request(url) {
    const response = await fetch(url, {signal:AbortSignal.timeout(6000), credentials:url.startsWith('/') ? 'same-origin' : 'omit', referrerPolicy:'no-referrer'});
    if (!response.ok) throw new Error('Currency lookup unavailable');
    return response.json();
  }

  async function loadRates() {
    const cached = storage.get('tb-display-rates-v1');
    const usable = data => data?.result === 'success' && data.base_code === 'USD' && Number(data.time_last_update_unix) * 1000 > Date.now() - 3 * day && Number(data.time_last_update_unix) * 1000 < Date.now() + day && supported.every(code => Number.isFinite(data.rates?.[code]) && data.rates[code] > 0) && data.rates.USD === 1;
    let data;
    if (usable(cached?.data) && cached.fetched > Date.now() - day) data = cached.data;
    else {
      try {
        data = await request('https://open.er-api.com/v6/latest/USD');
        if (!usable(data)) throw new Error('Currency rates unavailable');
        storage.set('tb-display-rates-v1', {data, fetched:Date.now()});
      } catch {
        if (usable(cached?.data)) data = cached.data;
      }
    }
    if (!data) { unavailable = true; return; }
    rates = Object.fromEntries(supported.map(code => [code, data.rates[code]]));
    const date = new Date(data.time_last_update_unix * 1000).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric',timeZone:'UTC'});
    writeText(bar.querySelector('[data-tb-rate-date]'), config.strings.updated.replace('[date]', date));
  }

  async function detectedCurrency() {
    return currencyForCountry(await detectVisitorCountry(config.root));
  }

  if (config.base === 'USD') {
    const pending = new Set();
    let scheduled = false;
    const observer = new MutationObserver(records => {
      for (const record of records) {
        if (record.type === 'characterData') {
          const previous = tracked.get(record.target);
          if (!previous || record.target.nodeValue !== previous.rendered) pending.add(record.target);
        } else {
          for (const node of record.addedNodes) pending.add(node);
        }
      }
      if (!pending.size || scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const root of pending) if (root.isConnected) walk(root);
        pending.clear();
        updateDisclosure();
      });
    });
    observer.observe(document.body, {subtree:true, childList:true, characterData:true});
    select.addEventListener('change', () => setCurrency(select.value, true));
    const countryPromise = manual ? Promise.resolve(saved.currency) : detectedCurrency();
    await loadRates();
    select.disabled = false;
    bar.hidden = false;
    setCurrency(manual ? saved.currency : 'USD');
    const detected = await countryPromise;
    if (!manual) setCurrency(detected);
  }
}

